import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Package, MapPin, Truck, Globe, AlertTriangle, ShieldOff } from 'lucide-react';
import { useBranding } from '../context/BrandingContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PublicItemPage = () => {
  const { token } = useParams();
  const { branding } = useBranding();
  const appName = branding?.app_name || 'BOM Maestro';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    axios.get(`${BACKEND}/api/public/items/${token}`)
      .then(({ data }) => setData(data))
      .catch(() => setError('This item link is inactive or has been revoked.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="spinner" /></div>;
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
        <div className="text-center max-w-sm">
          <ShieldOff className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-700">{error || 'Link not found'}</h1>
          <p className="text-sm text-slate-500 mt-2">If you scanned a printed QR, please contact the inventory manager.</p>
        </div>
      </div>
    );
  }

  const i = data.item;
  const lowStock = (i.reorder_point || 0) > 0 && (i.current_quantity || 0) <= (i.reorder_point || 0);
  const oos = (i.current_quantity || 0) <= 0;
  const cooLabel = i.country_of_origin_label || (i.country_of_origin === 'Other' ? 'Other / Unknown' : i.country_of_origin) || '—';

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="public-item-page">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="text-center">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{appName} · Public Item Card</div>
          <h1 className="text-2xl font-bold text-slate-800 break-all" data-testid="public-item-name">{i.item_name}</h1>
          <div className="text-sm font-mono text-slate-500 mt-0.5" data-testid="public-item-sku">{i.item_sku}</div>
        </div>

        <Card className="industrial-card">
          <CardContent className="p-0">
            {i.image_data && (
              <img src={i.image_data} alt={i.item_name} className="w-full max-h-72 object-contain bg-slate-100 border-b border-slate-200" />
            )}
            <div className="p-4 grid sm:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Category</div>
                <div>{i.item_category} · {i.item_type}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Unit of Measure</div>
                <div>{i.base_uom}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">On-hand</div>
                <div className={`font-bold mono ${oos ? 'text-red-600' : lowStock ? 'text-amber-600' : 'text-slate-800'}`} data-testid="public-on-hand">
                  {i.current_quantity} {i.base_uom}
                  {oos && <Badge className="ml-2 bg-red-600">Out of stock</Badge>}
                  {!oos && lowStock && <Badge className="ml-2 bg-amber-500">Low stock</Badge>}
                </div>
                <div className="text-xs text-slate-500">Reorder at {i.reorder_point} {i.base_uom}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Lead time</div>
                <div className="mono">{i.lead_time_days} days</div>
              </div>
              {i.description && (
                <div className="sm:col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Description</div>
                  <div className="text-xs whitespace-pre-wrap">{i.description}</div>
                </div>
              )}
              <div className="sm:col-span-2 grid grid-cols-3 gap-2 pt-2 border-t border-slate-100 text-xs">
                <div className="flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-slate-500">Country of Origin:</span>
                  <span className="font-medium" data-testid="public-coo">{cooLabel}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-slate-500">Location:</span>
                  <span className="font-medium">{data.location || '—'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Truck className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-slate-500">Vendor:</span>
                  <span className="font-medium">{data.vendor?.vendor_name || '—'}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {data.recent_receipts && data.recent_receipts.length > 0 && (
          <Card className="industrial-card">
            <CardHeader className="pb-2 border-b border-slate-100">
              <CardTitle className="text-sm flex items-center gap-2"><Package className="w-4 h-4" /> Recent Receipts</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {data.recent_receipts.map((s, idx) => {
                const issue = s.was_damaged || s.was_delayed;
                return (
                  <div key={idx} className="px-3 py-2 border-b border-slate-100 last:border-b-0 text-xs flex items-center justify-between">
                    <div>
                      <div className="font-medium">+{s.quantity} {s.batch_lot ? `· lot ${s.batch_lot}` : ''}</div>
                      <div className="text-slate-500 mono">{s.vendor_name || ''} · {s.carrier === 'Other' ? 'Other' : (s.carrier || '—')}</div>
                    </div>
                    <div className="text-right">
                      <div>{s.received_at ? new Date(s.received_at).toLocaleDateString() : ''}</div>
                      {s.landed_unit_cost_usd ? <div className="mono text-emerald-700">${Number(s.landed_unit_cost_usd).toFixed(2)}</div> : null}
                      {issue && <AlertTriangle className="w-3 h-3 text-amber-500 inline" />}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        <p className="text-center text-[10px] text-slate-400 pt-2">Read-only view · No data is editable from this page.</p>
      </div>
    </div>
  );
};

export default PublicItemPage;
